import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Divider,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import { useSelector } from "react-redux";
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";

/**
 * Patients information page component
 * @returns {JSX.Element}
 * @component
 */
const Patients = () => {
  const [expandedPanel, setExpandedPanel] = useState(0);

  // Extract state data with safe access patterns
  const { pageContents } = useSelector((state) => state) || {};
  const { patients: patientsPageContent } = pageContents || {};
  const patientsData = patientsPageContent?.data;

  // Get loading state from Redux
  const isLoading = useSelector(
    (state) => state.pageContents?.loading || !state.pageContents?.patients,
  );

  // Handle accordion expansion
  const handleAccordionChange = (panel) => (event, isExpanded) =>
    setExpandedPanel(isExpanded ? panel : -1);

  return (
    <PageLayout
      pageName="Patient Information"
      showLoader={isLoading}
      checkComingSoon
    >
      {/* Banner */}
      {patientsData?.patientbanner ? (
        <PageBanner
          bannerImage={patientsData.patientbanner.banner_image}
          mainTitle={
            patientsData.patientbanner.banner_title || "Patient Information"
          }
          pageTitle="Patients"
        />
      ) : (
        <PageBanner mainTitle="Patient Information" pageTitle="Patients" />
      )}

      {/* Content Section */}
      <Box component="section" sx={{ py: 8, bgcolor: "#f9fbfc" }}>
        <Container maxWidth="lg">
          {/* Section Header */}
          {patientsData?.patientorysection && (
            <Box sx={{ mb: 6, textAlign: "center" }}>
              <Typography variant="h4" component="h1" gutterBottom>
                {patientsData.patientorysection.section_title}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: "800px", mx: "auto" }}
              >
                {patientsData.patientorysection.section_description}
              </Typography>
            </Box>
          )}

          {/* FAQ Accordion */}
          <Paper
            elevation={2}
            sx={{
              maxWidth: "900px",
              mx: "auto",
              p: { xs: 2, sm: 4 },
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
              </Box>
            ) : patientsData?.patientqueAnswer?.length > 0 ? (
              patientsData.patientqueAnswer.map((item, index) => (
                <React.Fragment key={index}>
                  {index > 0 && <Divider sx={{ my: 1 }} />}
                  <Accordion
                    expanded={expandedPanel === index}
                    onChange={handleAccordionChange(index)}
                    disableGutters
                    elevation={0}
                    sx={{
                      "&:before": { display: "none" }, // Remove default divider
                      bgcolor: "transparent",
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`panel${index}-content`}
                      id={`panel${index}-header`}
                      sx={{
                        fontWeight: "medium",
                        color: "primary.main",
                        "&.Mui-expanded": {
                          color: "primary.dark",
                        },
                      }}
                    >
                      <Typography variant="h6">
                        {item.section_quetion}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, pb: 3 }}>
                      <Box
                        dangerouslySetInnerHTML={{
                          __html: item.section_answer,
                        }}
                        sx={{
                          "& p": { mb: 2, lineHeight: 1.7 },
                          "& ul, & ol": {
                            mb: 2,
                            pl: 2,
                            "& li": { mb: 1 },
                          },
                          "& a": {
                            color: "primary.main",
                            textDecoration: "none",
                            "&:hover": { textDecoration: "underline" },
                          },
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>
                </React.Fragment>
              ))
            ) : (
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ textAlign: "center", py: 4 }}
              >
                No patient information available at this time.
              </Typography>
            )}
          </Paper>
        </Container>
      </Box>
    </PageLayout>
  );
};

export default Patients;
